import { PDFDocument, degrees } from "pdf-lib";

import { type Annotation, drawAnnotations, preloadImages } from "./annotations";

const MAX_OVERLAY_SIDE = 4096;
const OVERLAY_SCALE = 3;

export type DocKind = "pdf" | "image";

export function docKindOf(name: string | undefined | null): DocKind | null {
  const ext = (name || "").split("?")[0].split(".").pop()?.toLowerCase();
  if (ext === "pdf") return "pdf";
  if (ext && ["png", "jpg", "jpeg", "webp", "gif", "bmp"].includes(ext)) return "image";
  return null;
}

export function annotatedFileName(name: string, suffix: string, ext: string) {
  const dot = name.lastIndexOf(".");
  const base = dot > 0 ? name.slice(0, dot) : name;
  return `${base}-${suffix}.${ext}`;
}

function canvasToBlob(canvas: HTMLCanvasElement, type = "image/png") {
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), type),
  );
}

export function displayedSize(width: number, height: number, rotation: number) {
  return rotation % 180 === 0 ? { width, height } : { width: height, height: width };
}

async function renderOverlay(annotations: Annotation[], width: number, height: number) {
  const scale = Math.min(OVERLAY_SCALE, MAX_OVERLAY_SIDE / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext("2d")!;
  ctx.scale(canvas.width / width, canvas.height / height);
  drawAnnotations(ctx, annotations);
  return new Uint8Array(await (await canvasToBlob(canvas)).arrayBuffer());
}

export async function exportAnnotatedPdf(
  source: ArrayBuffer | Uint8Array,
  annotations: Annotation[],
): Promise<Uint8Array> {
  await preloadImages(annotations);
  const pdf = await PDFDocument.load(source, { ignoreEncryption: true });
  const pages = pdf.getPages();
  for (let index = 0; index < pages.length; index++) {
    const own = annotations.filter((a) => a.page === index);
    if (!own.length) continue;
    const page = pages[index];
    const rotation = (((page.getRotation().angle % 360) + 360) % 360) as 0 | 90 | 180 | 270;
    const crop = page.getCropBox();
    const shown = displayedSize(crop.width, crop.height, rotation);
    const png = await pdf.embedPng(await renderOverlay(own, shown.width, shown.height));
    const origin = {
      0: { x: crop.x, y: crop.y },
      90: { x: crop.x + crop.width, y: crop.y },
      180: { x: crop.x + crop.width, y: crop.y + crop.height },
      270: { x: crop.x, y: crop.y + crop.height },
    }[rotation];
    page.drawImage(png, {
      ...origin,
      width: shown.width,
      height: shown.height,
      rotate: degrees(rotation),
    });
  }
  return pdf.save();
}

export async function exportAnnotatedImage(
  img: HTMLImageElement,
  annotations: Annotation[],
): Promise<Blob> {
  await preloadImages(annotations);
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0);
  drawAnnotations(ctx, annotations.filter((a) => a.page === 0));
  return canvasToBlob(canvas);
}

export function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
